import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h3>{`Description`}</h3>
    <p>{`All npm packages have a name. Some package names also have a scope. A scope
follows the usual rules for package names (URL-safe characters, no leading dots
or underscores). When used in package names, scopes are preceded by an `}<inlineCode parentName="p">{`@`}</inlineCode>{` symbol
and followed by a slash, e.g.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`@somescope/somepackagename
`}</code></pre>
    <p>{`Scopes are a way of grouping related packages together, and also affect a few
things about the way npm treats the package.`}</p>
    <p>{`Each npm user/organization has their own scope, and only you can add packages
in your scope. This means you don't have to worry about someone taking your
package name ahead of you. Thus it is also a good way to signal official packages
for organizations.`}</p>
    <p>{`Scoped packages can be published and installed as of `}<inlineCode parentName="p">{`npm@2`}</inlineCode>{` and are supported
by the primary npm registry. Unscoped packages can depend on scoped packages and
vice versa. The npm client is backwards-compatible with unscoped registries,
so it can be used to work with scoped and unscoped registries at the same time.`}</p>
    <h3>{`Installing scoped packages`}</h3>
    <p>{`Scoped packages are installed to a sub-folder of the regular installation
folder, e.g. if your other packages are installed in `}<inlineCode parentName="p">{`node_modules/packagename`}</inlineCode>{`,
scoped modules will be installed in `}<inlineCode parentName="p">{`node_modules/@myorg/packagename`}</inlineCode>{`. The scope
folder (`}<inlineCode parentName="p">{`@myorg`}</inlineCode>{`) is simply the name of the scope preceded by an `}<inlineCode parentName="p">{`@`}</inlineCode>{` symbol, and can
contain any number of scoped packages.`}</p>
    <p>{`A scoped package is installed by referencing it by name, preceded by an
`}<inlineCode parentName="p">{`@`}</inlineCode>{` symbol, in `}<inlineCode parentName="p">{`npm install`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm install @myorg/mypackage
`}</code></pre>
    <p>{`Or in `}<inlineCode parentName="p">{`package.json`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json"
      }}>{`"dependencies": {
  "@myorg/mypackage": "^1.3.0"
}
`}</code></pre>
    <p>{`Note that if the `}<inlineCode parentName="p">{`@`}</inlineCode>{` symbol is omitted, in either case, npm will instead attempt to
install from GitHub; see `}<a parentName="p" {...{
        "href": "/cli/v8/commands/npm-install"
      }}><inlineCode parentName="a">{`npm install`}</inlineCode></a>{`.`}</p>
    <h3>{`Requiring scoped packages`}</h3>
    <p>{`Because scoped packages are installed into a scope folder, you have to
include the name of the scope when requiring them in your code, e.g.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`require('@myorg/mypackage')
`}</code></pre>
    <p>{`There is nothing special about the way Node treats scope folders. This
simply requires the `}<inlineCode parentName="p">{`mypackage`}</inlineCode>{` module in the folder named `}<inlineCode parentName="p">{`@myorg`}</inlineCode>{`.`}</p>
    <h3>{`Publishing scoped packages`}</h3>
    <p>{`Scoped packages can be published from the CLI as of `}<inlineCode parentName="p">{`npm@2`}</inlineCode>{` and can be
published to any registry that supports them, including the primary npm
registry.`}</p>
    <p>{`(As of 2015-04-19, and with npm 2.0 or better, the primary npm registry
`}<strong parentName="p">{`does`}</strong>{` support scoped packages.)`}</p>
    <p>{`If you wish, you may associate a scope with a registry; see below.`}</p>
    <h4>{`Publishing public scoped packages to the primary npm registry`}</h4>
    <p>{`Publishing to a scope, you have two options:`}</p>
    <ul>
      <li parentName="ul">{`Publishing to your user scope (example: `}<inlineCode parentName="li">{`@username/module`}</inlineCode>{`)`}</li>
      <li parentName="ul">{`Publishing to an organization scope (example: `}<inlineCode parentName="li">{`@org/module`}</inlineCode>{`)`}</li>
    </ul>
    <p>{`If publishing a public module to an organization scope, you must
first either create an organization with the name of the scope
that you'd like to publish to or be added to an existing organization
with the appropriate permisssions. For example, if you'd like to
publish to `}<inlineCode parentName="p">{`@org`}</inlineCode>{`, you would  need to create the `}<inlineCode parentName="p">{`org`}</inlineCode>{` organization
on npmjs.com prior to trying to publish.`}</p>
    <p>{`Scoped packages are not public by default.  You will need to specify
`}<inlineCode parentName="p">{`--access public`}</inlineCode>{` with the initial `}<inlineCode parentName="p">{`npm publish`}</inlineCode>{` command.  This will publish
the package and set access to `}<inlineCode parentName="p">{`public`}</inlineCode>{` as if you had run `}<inlineCode parentName="p">{`npm access public`}</inlineCode>{`
after publishing.  You do not need to do this when publishing new versions of
an existing scoped package.`}</p>
    <h4>{`Publishing private scoped packages to the npm registry`}</h4>
    <p>{`To publish a private scoped package to the npm registry, you must have
an `}<a parentName="p" {...{
        "href": "https://docs.npmjs.com/private-modules/intro"
      }}>{`npm Private Modules`}</a>{`
account.`}</p>
    <p>{`You can then publish the module with `}<inlineCode parentName="p">{`npm publish`}</inlineCode>{` or `}<inlineCode parentName="p">{`npm publish
--access restricted`}</inlineCode>{`, and it will be present in the npm registry, with
restricted access. You can then change the access permissions, if
desired, with `}<inlineCode parentName="p">{`npm access`}</inlineCode>{` or on the npmjs.com website.`}</p>
    <h3>{`Associating a scope with a registry`}</h3>
    <p>{`Scopes can be associated with a separate registry. This allows you to
seamlessly use a mix of packages from the primary npm registry and one or more
private registries, such as `}<a parentName="p" {...{
        "href": "https://github.com/features/packages"
      }}>{`GitHub Packages`}</a>{` or the open source `}<a parentName="p" {...{
        "href": "https://verdaccio.org"
      }}>{`Verdaccio`}</a>{`
project.`}</p>
    <p>{`You can associate a scope with a registry at login, e.g.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm login --registry=http://reg.example.com --scope=@myco
`}</code></pre>
    <p>{`Scopes have a many-to-one relationship with registries: one registry can
host multiple scopes, but a scope only ever points to one registry.`}</p>
    <p>{`You can also associate a scope with a registry using `}<inlineCode parentName="p">{`npm config`}</inlineCode>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`npm config set @myco:registry http://reg.example.com
`}</code></pre>
    <p>{`Once a scope is associated with a registry, any `}<inlineCode parentName="p">{`npm install`}</inlineCode>{` for a package
with that scope will request packages from that registry instead. Any
`}<inlineCode parentName="p">{`npm publish`}</inlineCode>{` for a package name that contains the scope will be published to
that registry instead.`}</p>
    <h3>{`See also`}</h3>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-install"
        }}>{`npm install`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-publish"
        }}>{`npm publish`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/commands/npm-access"
        }}>{`npm access`}</a></li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/cli/v8/using-npm/registry"
        }}>{`npm registry`}</a></li>
    </ul>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      